import { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';
import { radius } from '../../theme/radius';

export type CitationBadgeVariant = 'default' | 'primary' | 'spotlight';

export type CitationBadgeProps = {
  /** The citation index number to display */
  index: number;
  /** Position coordinates for absolute positioning */
  position?: { left: number; top: number };
  /** Whether the badge is for the currently active highlight */
  isActive?: boolean;
  /** Visual variant of the badge */
  variant?: CitationBadgeVariant;
  /** Optional wrapper element (e.g., Tooltip) */
  wrapper?: (children: ReactNode) => ReactNode;
  /** Custom styles to override defaults */
  style?: CSSProperties;
  /** Custom class name */
  className?: string;
};

const StyledBadge = styled.div<{
  $isActive: boolean;
  $variant: CitationBadgeVariant;
  $hasPosition: boolean;
}>`
  position: ${({ $hasPosition }) => ($hasPosition ? 'absolute' : 'relative')};
  z-index: 1000;
  font-size: 11px;
  padding: 3px;
  border-radius: ${radius.pill}px;
  min-width: 20px;
  text-align: center;
  width: fit-content;
  font-weight: bold;
  opacity: 0.8;

  ${({ $variant, $isActive }) => {
    switch ($variant) {
      case 'default':
        return `
          color: #000;
          background: #22FF0B;
          opacity: 1;
        `;
      case 'primary':
        return `
          color: #fff;
          background: ${$isActive ? 'var(--ant-color-primary, #1890ff)' : 'var(--ant-color-bg-spotlight, #4a4a4a)'};
        `;
      case 'spotlight':
      default:
        return `
          color: #fff;
          background: ${$isActive ? 'var(--ant-color-primary, #1890ff)' : 'var(--ant-color-bg-spotlight, #4a4a4a)'};
        `;
    }
  }}
`;

/**
 * Badge component to display citation/highlight index numbers on PDF documents.
 *
 * Renders a numbered badge that can be positioned absolutely over highlights
 * or used inline. Supports multiple visual variants and optional wrapper
 * elements (e.g., for tooltips).
 *
 * @param props - Component props
 * @returns Citation badge element
 *
 * @example
 * ```tsx
 * // Basic usage with absolute positioning
 * <CitationBadge
 *   index={1}
 *   position={{ left: 100, top: 50 }}
 * />
 *
 * // With tooltip wrapper
 * <CitationBadge
 *   index={2}
 *   isActive={true}
 *   variant="primary"
 *   wrapper={(children) => <Tooltip title="Citation 2">{children}</Tooltip>}
 * />
 * ```
 */
export const CitationBadge = ({
  index,
  position,
  isActive = false,
  variant = 'spotlight',
  wrapper,
  style,
  className,
}: CitationBadgeProps) => {
  const positionStyle: CSSProperties = position
    ? { left: position.left, top: position.top }
    : {};

  const badge = (
    <StyledBadge
      $isActive={isActive}
      $variant={variant}
      $hasPosition={Boolean(position)}
      style={{ ...positionStyle, ...style }}
      className={className}
    >
      {index}
    </StyledBadge>
  );

  return wrapper ? wrapper(badge) : badge;
};

export default CitationBadge;
